import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import toColorString from 'polished/lib/color/toColorString';
import { color } from '@decisiv/design-tokens';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Button from '@decisiv/ui-components/lib/components/Button';
import Alert from '@decisiv/ui-components/lib/components/Alert';
import Loading from '@decisiv/ui-components/lib/components/Loading';
import { H1, H4, P } from '@decisiv/ui-components/lib/components/Typography';
import Copy from '@decisiv/iconix/lib/components/Copy';
import Search from '@decisiv/iconix/lib/components/Search';

import KeyDSPageContainer from 'components/KeyDSPageContainer';
import { performSearch } from './utils/searchService';
import { formatEventsForClipboard } from './utils/formatters';
import { parseUrlParams, pushUrlParams } from './utils/urlParams';
import SearchForm from './components/SearchForm';
import EventList from './components/EventList';
import { ResultsHeader } from './components/styledComponents';

function ErrorLogLookup({ csrfToken, searchUrl }) {
  const [searchData, setSearchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const urlParams = parseUrlParams();

  const handleSearch = useCallback(
    async ({ errorId, traceId, timeRange }) => {
      setLoading(true);
      setError(null);
      setSearchData(null);

      pushUrlParams({ errorId, traceId, timeRange });

      try {
        const data = await performSearch({
          searchUrl,
          csrfToken,
          errorId,
          traceId,
          timeRange,
        });

        if (data) {
          setSearchData(data);
        }
      } catch (err) {
        setError(err.message || 'Search failed.');
      } finally {
        setLoading(false);
      }
    },
    [searchUrl, csrfToken],
  );

  useEffect(() => {
    if (urlParams.errorId) {
      handleSearch({
        errorId: urlParams.errorId,
        traceId: urlParams.traceId,
        timeRange: urlParams.timeRange,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = useCallback(() => {
    if (!searchData || !searchData.events) return;

    const text = formatEventsForClipboard(
      searchData.events,
      searchData.anchor_id,
    );
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard.');
      });
  }, [searchData]);

  const eventCount =
    searchData && searchData.events ? searchData.events.length : 0;

  return (
    <KeyDSPageContainer>
      <Flex alignItems="center" marginBottom={2}>
        <Flex alignItems="center" style={{ flexShrink: 0 }}>
          <Search
            size="large"
            color={toColorString(color.interaction.pacificOcean)}
          />
        </Flex>
        <H1 marginLeft={0.5}>Error Log Lookup</H1>
      </Flex>

      <SearchForm
        onSearch={handleSearch}
        loading={loading}
        initialErrorId={urlParams.errorId}
        initialTraceId={urlParams.traceId}
        initialTimeRange={urlParams.timeRange}
      />

      {error && (
        <Flex marginTop={2}>
          <Alert intent="danger" title="Search failed" dismissible>
            {error}
          </Alert>
        </Flex>
      )}

      {loading && (
        <Flex
          justifyContent="center"
          alignItems="center"
          marginTop={4}
          paddingY={4}
        >
          <Loading
            size="medium"
            title="Searching..."
            description="Querying log events — this may take 5-15 seconds"
          />
        </Flex>
      )}

      {searchData && !loading && (
        <Flex flexDirection="column" marginTop={2}>
          <ResultsHeader
            justifyContent="space-between"
            alignItems="center"
            paddingBottom={1}
            marginBottom={1}
          >
            <Flex alignItems="baseline">
              <H4>
                {eventCount} {eventCount === 1 ? 'event' : 'events'} found
              </H4>
              {searchData.message && (
                <P shade={1} marginLeft={0.5}>
                  {' '}
                  — {searchData.message}
                </P>
              )}
            </Flex>
            <Button
              icon={Copy}
              text={copySuccess ? 'Copied' : 'Copy All'}
              variant="ghost"
              size="small"
              onClick={handleCopy}
              intent={copySuccess ? 'success' : undefined}
            />
          </ResultsHeader>

          {eventCount > 0 ? (
            <EventList
              events={searchData.events}
              anchorId={searchData.anchor_id}
              errorIds={searchData.error_ids || []}
            />
          ) : (
            <Flex justifyContent="center" paddingY={4}>
              <P shade={1}>No log events matched your search criteria.</P>
            </Flex>
          )}
        </Flex>
      )}
    </KeyDSPageContainer>
  );
}

ErrorLogLookup.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  searchUrl: PropTypes.string.isRequired,
};

export default ErrorLogLookup;
